import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, apiError, formatApiError, uploadFiles } from "@/lib/api";
import { useExportDownload } from "@/hooks/useExportDownload";
import { PhotoPinViewer, photoInfo } from "@/components/defects/PhotoPinViewer";
import { shareToWhatsApp } from "@/components/gallery/ShareSheet";
import { GalleryGrid } from "@/components/gallery/GalleryGrid";
import { plur, formatDate } from "@/lib/format";
import { useAuth, canLeadTasks } from "@/context/AuthContext";
import { DefectTaskForm } from "@/components/defects/DefectTaskForm";
import { Button } from "@/components/ui/button";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, CloudSun, Users, Hammer, StickyNote, Camera, ChevronLeft, ChevronRight, ChevronDown, Save, FileDown, Plus, Trash2, Sparkles, Wand2, X, Check, EyeOff, Package, Printer, CalendarRange, BookOpen, Loader2 } from "lucide-react";
import { usePhotoInputs } from "@/components/shared/PhotoPicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuCheckboxItem } from "@/components/ui/dropdown-menu";
import { ask } from "@/lib/confirm";
import { DiaryCrew } from "@/components/diary/DiaryCrew";

const MAT_BADGE = {
  pending: { cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400", label: "čeká na odpis" },
  confirmed: { cls: "bg-success/15 text-success", label: "odepsáno ze skladu" },
  dismissed: { cls: "bg-muted text-muted-foreground", label: "odepsáno ručně" },
  noted: { cls: "bg-secondary text-secondary-foreground", label: "mimo sklad" },
};

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmtQty = (n) => Number(n || 0).toLocaleString("cs-CZ", { maximumFractionDigits: 3 });
// Vše v UTC! Původní verze parsovala lokálně a vracela přes toISOString (UTC):
// v Praze (UTC+2) tak „+1 den" vrátil TENTÝŽ den a smyčka skládající pás dní
// se zacyklila — celá stránka Deník zamrzla.
const addDaysISO = (iso, n) => { const d = new Date(iso + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const fmtCzDate = (iso) => {
  try {
    const d = new Date(iso + "T00:00:00");
    const dows = ["Ne", "Po", "Út", "St", "Čt", "Pá", "So"];
    return `${dows[d.getDay()]} ${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
  } catch { return iso; }
};
const emptyWeather = { t07: "", d07: "", t12: "", d12: "", t17: "", d17: "", tmin: "", tmax: "", extra: [] };
const WX_KEYS = ["t07", "d07", "t12", "d12", "t17", "d17", "tmin", "tmax"];
const DOW = ["Po", "Út", "St", "Čt", "Pá", "So", "Ne"];
// Podklady dne bez zápisu (zadání 144) — vyjmenovat jen to, co opravdu existuje.
const peopleNom = (n) => plur(n, "člověk", "lidi", "lidí");
const peopleGen = (n) => (n === 1 ? "1 člověka" : `${n} lidí`);
const pendingTitle = (f) => {
  const parts = [];
  if (f.attendance_count > 0) parts.push(`docházka (${peopleNom(f.attendance_count)})`);
  if (f.has_photos_any) parts.push("fotky");
  if (f.has_material) parts.push("materiál");
  const lead = parts.length === 1 && f.attendance_count > 0 ? "Je zapsaná" : "Podklady:";
  return `${lead} ${parts.join(", ")} — deník zatím není napsaný.`;
};

export function ProjectDiary({ projectId }) {
  const { user } = useAuth();
  // Asistent/ka vidí deník jen pro čtení — nemůže vytvářet ani upravovat zápisy.
  const readOnly = user?.role === "ASSISTANT";
  // Projektant (173) deník píše, ale materiál/sklad neřeší — tahle část je pro něj jen ke čtení.
  const matReadOnly = readOnly || user?.role === "DESIGNER";
  // 409: partu (lidi z týmu → schválená docházka) zapisuje majitel a stavbyvedoucí
  const canCrew = !readOnly && (user?.role === "OWNER" || user?.role === "MANAGER");
  const [crewNames, setCrewNames] = useState(() => new Set());
  const onCrewRows = useCallback((rows) => setCrewNames(new Set(rows.map((r) => r.name))), []);
  const [crewCount, setCrewCount] = useState(0);
  const crewRef = useRef(null);
  // 302: přehled deníku otevírá konkrétní den (?date=YYYY-MM-DD); jinak dnešek
  const [searchParams] = useSearchParams();
  const [date, setDate] = useState(() => {
    const d = searchParams.get("date") || "";
    return /^\d{4}-\d{2}-\d{2}$/.test(d) && d <= todayISO() ? d : todayISO();
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [meta, setMeta] = useState({ hasGps: true, prefillWorkers: [], suggestion: "", people: [] });
  const [openPerson, setOpenPerson] = useState(null);   // 186: rozbalený řádek „Kdo co dělal"
  const [savedAt, setSavedAt] = useState(null);
  const [author, setAuthor] = useState(null);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [range, setRange] = useState({ from: todayISO(), to: todayISO() });
  const [form, setForm] = useState({ weather: { ...emptyWeather }, workers: [], work_performed: "", notes: "" });
  // 257: počasí se doplní samo při otevření dne (zakázka s GPS) — jen hodiny, které už
  // proběhly (server vrací jen známá data), a jen do prázdných polí; ruční hodnoty se nepřepíšou.
  // Místo „Načíst počasí" je „Přidat hodinu" pro vlastní měření.
  const dateRef = useRef(date);
  dateRef.current = date;
  // 331: když se počasí nepodaří načíst, ukáže se to (dřív se chyba zahodila) a jde to zkusit znovu
  const [wxErr, setWxErr] = useState(false);
  const [wxBusy, setWxBusy] = useState(false);
  const autoWeather = useCallback(async (d) => {
    setWxBusy(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/diary/${d}/weather`);
      if (dateRef.current !== d) return;
      setWxErr(false);
      setForm((f) => {
        const w = { ...f.weather };
        WX_KEYS.forEach((k) => { if ((w[k] === "" || w[k] == null) && data[k] != null) w[k] = data[k]; });
        return { ...f, weather: w };
      });
    } catch {
      if (dateRef.current === d) setWxErr(true);   // bez počasí — stavbyvedoucí ho napíše ručně nebo zkusí znovu
    } finally {
      if (dateRef.current === d) setWxBusy(false);
    }
  }, [projectId]);
  const setExtra = (i, k, v) => setForm((f) => ({ ...f, weather: { ...f.weather, extra: (f.weather.extra || []).map((x, j) => (j === i ? { ...x, [k]: v } : x)) } }));
  const addExtra = () => setForm((f) => ({ ...f, weather: { ...f.weather, extra: [...(f.weather.extra || []), { time: "", t: "", d: "" }] } }));
  const delExtra = (i) => setForm((f) => ({ ...f, weather: { ...f.weather, extra: (f.weather.extra || []).filter((_, j) => j !== i) } }));
  const [attPhotos, setAttPhotos] = useState([]);
  const [selPhotos, setSelPhotos] = useState(new Set());
  const [showRejected, setShowRejected] = useState(false); // 186: sbalené „Nepoužité"
  const [photoSel, setPhotoSel] = useState(false); // režim Vybrat (jako Galerie)
  const [matData, setMatData] = useState({ items: [], net_qty_by_item: [], has_returns: false });
  const [matStock, setMatStock] = useState([]);
  const [matDialog, setMatDialog] = useState(null); // {mode: "stock"|"free", stock_item_id, name, qty, unit, writeoff}
  const [matBusy, guardMat] = useSubmitGuard();   // dvojklik nesmí odepsat materiál dvakrát
  const [viewer, setViewer] = useState(null);       // 185: { urls, start } — jednotný prohlížeč fotek
  const [drafts, setDrafts] = useState([]);         // 187: košík závad (špendlíky) z fotek dne
  const [taskOpen, setTaskOpen] = useState(false);
  const [returnTask, setReturnTask] = useState(null); // 187: fotky z úkolu čekajícího na schválení
  const [projectName, setProjectName] = useState("");
  const [uploadProg, setUploadProg] = useState(null); // {done, total} — běžící nahrávání fotek
  const [strip, setStrip] = useState({});
  const stripRef = useRef(null);

  // Okno zápisu = zahájení … min(dnešek, dokončení + 7 dní) — stejné pravidlo
  // jako validace na serveru. Mimo okno se datum nedá vybrat, pás dny vůbec
  // nenabízí a formulář se neukazuje.
  const [win, setWin] = useState(null);   // { start, end } — null než se načte zakázka
  useEffect(() => {
    api.get(`/projects/${projectId}`).then((r) => {
      const p = r.data || {};
      setWin({ start: (p.start_date || "").slice(0, 10) || null, end: (p.end_date || "").slice(0, 10) || null });
    }).catch(() => setWin({ start: null, end: null }));
  }, [projectId]);
  const winMax = win?.end ? [addDaysISO(win.end, 7), todayISO()].sort()[0] : todayISO();
  const winMin = win?.start || null;
  const emptyWindow = !!(winMin && winMin > winMax);   // stavba ještě nezačala
  const inWindow = (d) => !emptyWindow && (!winMin || d >= winMin) && d <= winMax;

  // Vybraný den se po načtení zakázky přiskřípne do okna (dnešek může být mimo).
  useEffect(() => {
    if (!win || emptyWindow) return;
    setDate((d) => (d > winMax ? winMax : winMin && d < winMin ? winMin : d));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win]);

  // Pás ukazuje CELÉ období stavby (zahájení … dokončení), ať je vidět rozsah
  // zakázky. Dny po dnešku jsou v něm zamčené — deník se do budoucna nepíše.
  const stripDays = useMemo(() => {
    const first = winMin || addDaysISO(win ? winMax : todayISO(), -29);
    const last = win?.end && win.end > winMax ? win.end : winMax;
    const arr = [];
    // Pojistka: kdyby posun data z jakéhokoli důvodu nepostoupil, smyčka skončí.
    for (let d = first, i = 0; d <= last && i < 1200; d = addDaysISO(d, 1), i++) arr.push(d);
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [win]);

  const loadStrip = useCallback(async () => {
    try {
      const { data } = await api.get(`/projects/${projectId}/diary-strip?date_from=${stripDays[0]}&date_to=${stripDays[stripDays.length - 1]}`);
      setStrip(data.days || {});
    } catch { /* ignore */ }
  }, [projectId, stripDays]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/diary/${date}`);
      setMeta({ hasGps: data.has_gps, prefillWorkers: data.prefill.workers || [], suggestion: data.prefill.work_suggestion || "", people: data.prefill.people || [] });
      setAttPhotos(data.photos || data.attendance_photos || []);
      setProjectName(data.project_name || "");
      setSelPhotos(new Set());
      setWxErr(false);
      const wxGap = (w) => ["t07", "t12", "t17", "tmin", "tmax"].some((k) => w?.[k] == null || w[k] === "");
      if (data.has_gps && !readOnly && date <= todayISO() && wxGap(data.entry ? data.entry.weather : data.prefill.weather)) autoWeather(date);
      if (data.entry) {
        setForm({
          weather: { ...emptyWeather, ...(data.entry.weather || {}), extra: (data.entry.weather?.extra || []).map((x) => ({ time: x.time || "", t: x.t ?? "", d: x.d || "" })) },
          workers: data.entry.workers || [], work_performed: data.entry.work_performed || "",
          notes: data.entry.notes || "",
        });
        setSavedAt(data.entry.updated_at);
        setAuthor(data.entry.author_name);
      } else {
        setForm({
          weather: { ...emptyWeather, ...(data.prefill.weather || {}), extra: [] },
          workers: data.prefill.workers || [], work_performed: data.prefill.work_suggestion || "",
          notes: "",
        });
        setSavedAt(null);
        setAuthor(null);
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [projectId, date, readOnly, autoWeather]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadStrip(); }, [loadStrip, savedAt]);

  // Materiál dne (zadání 142): jediný zdroj pravdy je skladová kniha —
  // pohyby výdej/vratka daného dne + hlášení bez pohybu, nikdy dvakrát.
  const loadMaterials = useCallback(() => {
    api.get(`/projects/${projectId}/diary/${date}/materials`)
      .then((r) => setMatData(r.data || { items: [], net_qty_by_item: [], has_returns: false }))
      .catch(() => setMatData({ items: [], net_qty_by_item: [], has_returns: false }));
  }, [projectId, date]);
  useEffect(() => { loadMaterials(); }, [loadMaterials]);

  const openMatDialog = async (mode) => {
    setMatDialog({ mode, stock_item_id: "", name: "", qty: "", unit: "", writeoff: true });
    if (mode === "stock") {
      try { const { data } = await api.get(`/projects/${projectId}/stock-picker`); setMatStock(data || []); }
      catch { setMatStock([]); }
    }
  };

  const saveMatDialog = async () => {
    const d = matDialog;
    if (!d || !d.qty || Number(d.qty) <= 0) return toast.error("Zadejte kladné množství.");
    if (d.mode === "stock" && !d.stock_item_id) return toast.error("Vyberte položku skladu.");
    if (d.mode === "free" && !d.name.trim()) return toast.error("Zadejte název materiálu.");
    try {
      await api.post("/material-reports/manual", {
        project_id: projectId, date,
        stock_item_id: d.mode === "stock" ? d.stock_item_id : null,
        name: d.mode === "free" ? d.name.trim() : null,
        qty: Number(d.qty), unit: d.unit || null,
        writeoff: d.mode === "stock" ? d.writeoff : false,
      });
      toast.success(d.mode === "stock" && d.writeoff ? "Materiál vydán ze skladu." : "Materiál zapsán do deníku.");
      setMatDialog(null); loadMaterials(); loadStrip();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const resolveMat = async (rep, action) => {
    try {
      await api.post(`/material-reports/${rep.id}/${action}`, {});
      toast.success(action === "confirm" ? "Odepsáno ze skladu." : "Odškrtnuto bez pohybu skladu.");
      loadMaterials();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [matDel, setMatDel] = useState(null); // hlášení ke smazání (confirmed → storno s vratkou)
  const deleteMat = async () => {
    try {
      const { data } = await api.delete(`/material-reports/${matDel.id}`);
      toast.success(data.returned_qty > 0
        ? `Záznam smazán, ${data.returned_qty} ${matDel.unit} vráceno na sklad.`
        : "Záznam smazán.");
      setMatDel(null); loadMaterials();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  // Pás obsahuje i zamčenou budoucnost — po načtení se má vycentrovat dnešek.
  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const i = stripDays.indexOf(todayISO());
    el.scrollLeft = i >= 0 ? Math.max(0, i * 58 - el.clientWidth / 2 + 29) : el.scrollWidth;
  }, [strip, stripDays]);

  const shiftDay = (n) => setDate((d) => new Date(new Date(d + "T00:00:00").getTime() + n * 86400000).toISOString().slice(0, 10));
  const setW = (k, v) => setForm((f) => ({ ...f, weather: { ...f.weather, [k]: v } }));

  const syncFromAttendance = async () => {
    const manual = form.workers.filter((w) => w.manual);
    setForm((f) => ({
      ...f,
      workers: [...meta.prefillWorkers, ...manual],
      work_performed: f.work_performed.trim() ? f.work_performed : meta.suggestion,
    }));
    toast.success("Doplněno z docházky.");
  };

  // 409: po uložení party do docházky se v zápisu deníku přepíšou její řádky (auto) a zápis se uloží
  const onCrewSaved = (rows) => {
    const names = new Set([...crewNames, ...rows.map((r) => r.name)]);
    const crewW = rows.map((r) => ({ name: r.name, hours: Number(String(r.hours).replace(",", ".")), manual: false, role: r.position || "" }));
    const merged = [...form.workers.filter((w) => w.manual || !names.has(w.name)), ...crewW];
    setForm((f) => ({ ...f, workers: merged }));
    setMeta((m) => ({ ...m, prefillWorkers: [...m.prefillWorkers.filter((w) => !names.has(w.name)), ...crewW] }));
    save({ workers: merged, quiet: true });
  };
  const addManualWorker = () => setForm((f) => ({ ...f, workers: [...f.workers, { name: "", hours: "", manual: true, role: "" }] }));
  const setWorker = (i, k, v) => setForm((f) => { const w = [...f.workers]; w[i] = { ...w[i], [k]: v }; return { ...f, workers: w }; });
  const removeWorker = (i) => setForm((f) => ({ ...f, workers: f.workers.filter((_, idx) => idx !== i) }));

  const handlePhotos = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";   // reset na vstupu, který vystřelil (foťák i galerie) — stejný soubor jde vybrat znovu
    if (!files.length) return;
    setUploading(true);
    setUploadProg({ done: 0, total: files.length });
    // Každá fotka zvlášť: jedno selhání nesmí zahodit už nahrané. A důvod chyby
    // se musí dostat k uživateli — obecné „selhalo" se nedá diagnostikovat.
    // 216: až tři fotky naráz (uploadFiles), pořadí zůstává podle výběru.
    const res = await uploadFiles(files, { onEach: (done) => setUploadProg((p) => (p ? { ...p, done } : p)) });
    const urls = res.urls;
    const failed = res.failed.map(({ file, error }) => ({ name: file.name || "fotka", why: apiError(error) }));
    try {
      if (urls.length) {
        // Do banky ke dni deníku. 263: i vlastní fotky čekají na schválení — stavbyvedoucí
        // vybere, které půjdou do deníku a do PDF (dřív se schválily samy a šly do tisku všechny).
        const { data } = await api.post(`/projects/${projectId}/photos`, { urls, date, caption: "" });
        // Jen připojit — bez load() celého dne, jinak stránka uskočí nahoru (zadání 143/B).
        const ts = new Date().toISOString();
        setAttPhotos((arr) => [...arr, ...(data.ids || []).map((id, i) => ({
          id, url: urls[i], date, caption: "", author_id: user?.id, author_name: user?.name,
          source: "upload", status: "pending", approved_by_name: null, approved_at: null, created_at: ts,
        }))]);
        loadStrip();   // jen pás (podklady dne), ne celý den
      }
      if (!failed.length) {
        toast.success(`Přidáno ${plur(urls.length, "fotka", "fotky", "fotek")} — jsou v deníku.`);
      } else {
        toast.error(
          `Nahráno ${urls.length} z ${files.length}. Nepovedlo se: ${failed.map((x) => x.name).join(", ")} — ${failed[0].why}`,
          { duration: 12000 });
      }
    } catch (e3) { toast.error(formatApiError(e3.response?.data?.detail)); }
    setUploading(false);
    setUploadProg(null);
  };
  const photoPick = usePhotoInputs({ onChange: handlePhotos, disabled: uploading });

  const toggleSelPhoto = (id) => setSelPhotos((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  // Optimisticky, bez load() (vzor 138/B): načtení celého dne překreslí stránku
  // a prohlížeč ztratí pozici scrollu — po každém oku by uživatel skočil nahoru.
  const patchPhotos = (ids, status) =>
    setAttPhotos((arr) => arr.map((p) => (ids.includes(p.id) ? { ...p, status } : p)));
  // 186: status approved = V deníku, rejected = Nepoužitá (zůstává v Galerii), jinak čeká.
  const approvePhotos = async (ids, approved = true, { quiet = false, rejected = false } = {}) => {
    if (!ids.length) return false;
    const prev = attPhotos;
    patchPhotos(ids, rejected ? "rejected" : approved ? "approved" : "pending");
    try {
      await api.post("/diary/photos/approve", { photo_ids: ids, approved, rejected });
      if (!quiet) toast.success(rejected ? "Fotky nepoužity." : approved ? "Fotky schváleny do deníku." : "Schválení zrušeno.");
      return true;
    } catch (e) { setAttPhotos(prev); toast.error(formatApiError(e.response?.data?.detail)); return false; }
  };
  const bulkApprove = async (approved) => { if (await approvePhotos([...selPhotos], approved)) setSelPhotos(new Set()); };
  const deletePhotos = async () => {
    const ids = [...selPhotos];
    const n = ids.length;
    if (!await ask(`Opravdu smazat ${plur(n, "fotku", "fotky", "fotek")}? Zmizí z banky i z deníku.`)) return;
    const prev = attPhotos;
    setAttPhotos((arr) => arr.filter((p) => !ids.includes(p.id)));
    setSelPhotos(new Set());
    try {
      await api.post("/photos/delete", { photo_ids: ids });
      toast.success(`${plur(n, "fotka smazána", "fotky smazány", "fotek smazáno")}.`);
    } catch (e) { setAttPhotos(prev); setSelPhotos(new Set(ids)); toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // 187: košík závad → úkol. Jsou-li všechny závady na fotkách jednoho úkolu, který
  // čeká na schválení, nabídne se i Vrátit k opravě (v deníku nepoznáš, ke kterému úkolu fotka patří).
  const openDefectTask = async () => {
    setViewer(null); setReturnTask(null); setTaskOpen(true);
    const tids = [...new Set(drafts.map((d) => attPhotos.find((p) => p.url === d.photo_url)).map((p) => (p?.source === "task" ? p.task_id : null)))];
    if (tids.length === 1 && tids[0]) {
      try { const { data } = await api.get(`/tasks/${tids[0]}`); if (data?.status === "Hotovo") setReturnTask({ id: data.id, title: data.title }); } catch { /* jen nabídka */ }
    }
  };
  const draftAuthor = (() => { const p = attPhotos.find((x) => x.url === drafts[0]?.photo_url); return p?.author_id ? { id: p.author_id, name: p.author_name } : null; })();

  const save = async (over) => {
    const workersNow = over?.workers || form.workers;
    setSaving(true);
    try {
      const payload = {
        weather: {
          ...Object.fromEntries(WX_KEYS.map((k) => { const v = form.weather[k]; return [k, v === "" || v == null ? null : (k.startsWith("d") ? v : Number(v))]; })),
          extra: (form.weather.extra || []).filter((x) => x.time).sort((a, b) => (a.time < b.time ? -1 : 1))
            .map((x) => ({ time: x.time, t: x.t === "" || x.t == null ? null : Number(x.t), d: x.d || "" })),
        },
        workers: workersNow.filter((w) => w.name).map((w) => ({ name: w.name, hours: w.hours === "" ? 0 : Number(String(w.hours).replace(",", ".")), manual: !!w.manual, role: w.role || null })),
        work_performed: form.work_performed, notes: form.notes,
      };
      const { data } = await api.put(`/projects/${projectId}/diary/${date}`, payload);
      setSavedAt(data.updated_at); setAuthor(data.author_name);
      if (!over?.quiet) toast.success("Zápis uložen.");
      return true;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); return false; }
    finally { setSaving(false); }
  };

  // Tisk (zadání 151): jedna volba „S fotkami" platí pro den, období i celý deník; pamatuje se v localStorage.
  const [printPhotos, setPrintPhotos] = useState(() => localStorage.getItem("diary_print_photos") !== "0");
  const togglePrintPhotos = (v) => { setPrintPhotos(v); localStorage.setItem("diary_print_photos", v ? "1" : "0"); };
  const photosQ = printPhotos ? "" : "&photos=0";
  // 217: PDF se stahuje jako blob — během přípravy je Tisk zakázaný a běží hláška,
  // druhé kliknutí nespustí další export; chyba skončí toastem, ne přesměrováním.
  const { busy: printing, run: runPrint } = useExportDownload();
  const pdfUrl = (q) => `/api/projects/${projectId}/diary/export/pdf?${q}${photosQ}`;
  // 263: neuložený den by v PDF chyběl (stavbyvedoucí šel rovnou na Tisk a PDF bylo prázdné) —
  // když má rozepsaný a ještě nikdy neuložený zápis, před tiskem se sám uloží.
  const hasContent = () => !!(form.work_performed?.trim() || form.notes?.trim() || form.workers.some((w) => w.name)
    || WX_KEYS.some((k) => form.weather[k] !== "" && form.weather[k] != null) || (form.weather.extra || []).some((x) => x.time));
  const saveBeforePrint = async () => (readOnly || savedAt || !hasContent() ? true : save());
  const printDay = async () => { if (await saveBeforePrint()) runPrint("day", pdfUrl(`date_from=${date}&date_to=${date}`), `stavebni_denik_${date}.pdf`, "Připravuji PDF dne…"); };
  const printAll = async () => { if (await saveBeforePrint()) runPrint("all", pdfUrl("all=1"), "stavebni_denik_cely.pdf",
    printPhotos ? "Připravuji celý deník s fotkami — může to chvíli trvat…" : "Připravuji celý deník…"); };
  const printRange = async () => {
    if (!range.from || !range.to) { toast.error("Zadejte období od–do."); return; }
    if (range.from > range.to) { toast.error("Datum „od“ musí být před datem „do“."); return; }
    setRangeOpen(false);
    if (!(await saveBeforePrint())) return;
    runPrint("range", pdfUrl(`date_from=${range.from}&date_to=${range.to}`), `stavebni_denik_${range.from}_${range.to}.pdf`, "Připravuji PDF za období…");
  };

  return (
    <div className="space-y-4" data-testid="project-diary">
      {/* Date strip + tisk */}
      <div className="bg-card border border-border rounded-[20px] p-3 space-y-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="font-bold" data-testid="diary-current-date">{fmtCzDate(date)}</span>
            <Input type="date" value={date} min={winMin || undefined} max={winMax}
              onChange={(e) => {
                const v = e.target.value;
                if (v && win && !inWindow(v)) { toast.error("Deník lze psát jen v období stavby (od zahájení do 7 dní po dokončení)."); return; }
                setDate(v);
              }} className="h-8 w-36 text-xs" data-testid="diary-date-input" />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={!!printing} aria-busy={!!printing} data-testid="diary-print-btn">
                {printing ? <Loader2 size={14} className="animate-spin motion-reduce:animate-none" /> : <Printer size={14} />} Tisk <ChevronDown size={14} className="opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[220px]">
              <DropdownMenuItem onClick={printDay} data-testid="diary-print-day"><FileDown size={15} className="mr-2 text-brand" /> Tento den</DropdownMenuItem>
              <DropdownMenuItem onClick={() => setRangeOpen(true)} data-testid="diary-print-range"><CalendarRange size={15} className="mr-2 text-brand" /> Období od–do…</DropdownMenuItem>
              <DropdownMenuItem onClick={printAll} data-testid="diary-print-all"><BookOpen size={15} className="mr-2 text-brand" /> Celý deník</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuCheckboxItem checked={printPhotos} onCheckedChange={togglePrintPhotos} onSelect={(e) => e.preventDefault()} data-testid="diary-print-photos">
                S fotkami
              </DropdownMenuCheckboxItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-1">
          <Button variant="outline" size="icon" className="shrink-0 h-9 w-9" onClick={() => { if (stripRef.current) stripRef.current.scrollLeft -= 240; }} data-testid="diary-strip-left"><ChevronLeft size={16} /></Button>
          <div ref={stripRef} className="flex gap-1.5 overflow-x-auto no-scrollbar flex-1 py-1">
            {stripDays.map((d) => {
              const f = strip[d];
              const dt = new Date(d + "T00:00:00");
              const dow = (dt.getDay() + 6) % 7;
              const isSel = d === date;
              const isToday = d === todayISO();
              const locked = !inWindow(d);   // budoucnost — jen ukazuje rozsah stavby
              // Tři stavy (zadání 144): zapsáno / podklady bez zápisu (oranžově) / chybí (červeně).
              const pending = !locked && !!f?.pending;
              const hasEntry = !!f?.entry;
              const missing = !locked && !f && dow < 5;
              const amber = pending ? (isSel ? " ring-1 ring-amber-400" : " border-amber-500/60 bg-amber-500/10") : "";
              const tileCls = isSel ? "bg-brand text-white border-brand"
                : isToday ? (pending ? "ring-1 ring-brand" : "border-brand ring-1 ring-brand bg-card")
                : locked ? "bg-secondary/40 border-border/50 opacity-45 cursor-not-allowed"
                : pending ? "hover:border-amber-500" : "bg-card border-border hover:border-brand/60";
              const title = locked ? "Deník se otevře, až tento den nastane"
                : pending && !hasEntry ? pendingTitle(f)
                : pending ? `Zápis je rozepsaný — chybí „Provedené práce".` : undefined;
              return (
                <button key={d} disabled={locked} onClick={() => setDate(d)} data-testid={`diary-strip-${d}`}
                  data-state={pending && !hasEntry ? "pending" : pending ? "draft" : hasEntry ? "entry" : missing ? "missing" : "empty"}
                  title={title}
                  className={`shrink-0 w-[52px] rounded-[12px] border px-1 py-1.5 flex flex-col items-center gap-1 transition-colors ${tileCls}${amber}`}>
                  <span className={`text-[10px] font-bold uppercase ${isSel ? "text-white/80" : isToday ? "text-brand" : "text-muted-foreground"}`}>{isToday ? "dnes" : DOW[dow]}</span>
                  <span className="text-base font-extrabold leading-none">{dt.getDate()}</span>
                  <span className="flex items-center gap-0.5 h-3">
                    {hasEntry ? (
                      <>
                        <CloudSun size={10} className={f.weather ? (isSel ? "text-white" : "text-brand") : "opacity-25"} />
                        <Users size={10} className={f.workers ? (isSel ? "text-white" : "text-brand") : "opacity-25"} />
                        <Hammer size={10} className={f.work ? (isSel ? "text-white" : "text-brand") : "opacity-25"} />
                        <Camera size={10} className={f.photos ? (isSel ? "text-white" : "text-brand") : "opacity-25"} />
                      </>
                    ) : pending ? <span className={`w-1.5 h-1.5 rounded-full ${isSel ? "bg-amber-300" : "bg-amber-500"}`} data-testid={`diary-strip-pending-${d}`} />
                    : missing ? <span className="w-1.5 h-1.5 rounded-full bg-destructive" /> : <span className="w-1.5 h-1.5" />}
                  </span>
                </button>
              );
            })}
          </div>
          <Button variant="outline" size="icon" className="shrink-0 h-9 w-9" onClick={() => { if (stripRef.current) stripRef.current.scrollLeft += 240; }} data-testid="diary-strip-right"><ChevronRight size={16} /></Button>
        </div>
      </div>

      {win && (emptyWindow || !inWindow(date)) ? (
        <div className="bg-card border border-border rounded-[20px] p-8 text-center text-sm text-muted-foreground" data-testid="diary-out-of-window">
          {emptyWindow
            ? <>Deník se otevře se zahájením stavby <b className="text-foreground">{fmtCzDate(winMin)}</b>.</>
            : <>Deník lze psát jen v období stavby{winMin ? <>: <b className="text-foreground">{fmtCzDate(winMin)}</b></> : null} – <b className="text-foreground">{fmtCzDate(winMax)}</b> (7 dní po termínu dokončení). Vyber datum v tomto rozmezí.</>}
        </div>
      ) : loading ? (
        <div className="bg-card border border-border rounded-[20px] p-8 text-center text-muted-foreground">Načítám…</div>
      ) : (
        <>
          {/* Podklady dne bez zápisu (zadání 144) — jedna řádka nad formulářem */}
          {(() => {
            const f = strip[date];
            if (!f?.pending || f.entry) return null;
            const parts = [];
            if (f.attendance_count > 0) parts.push(<b key="a" className="text-foreground">docházka {peopleGen(f.attendance_count)}</b>);
            const nPh = attPhotos.length;
            if (nPh > 0 || f.has_photos_any) parts.push(<b key="p" className="text-foreground">{nPh > 0 ? plur(nPh, "fotka", "fotky", "fotek") : "fotky"}</b>);
            if (f.has_material) parts.push(<b key="m" className="text-foreground">materiál</b>);
            return (
              <div className="flex flex-col md:flex-row md:items-center gap-2 md:gap-3 bg-amber-500/10 border border-amber-500/30 rounded-[14px] px-4 py-3 text-sm" data-testid="diary-pending-banner">
                <p className="flex-1 min-w-0 text-foreground/90">
                  Za tento den {f.attendance_count > 0 && parts.length === 1 ? "je zapsaná" : "jsou k dispozici"}{" "}
                  {parts.map((p, i) => <span key={i}>{i > 0 && <span className="text-muted-foreground"> · </span>}{p}</span>)}
                  {" "}— deník zatím není napsaný.
                </p>
                {!readOnly && f.attendance_count > 0 && (
                  <Button variant="outline" size="sm" onClick={syncFromAttendance} className="w-full md:w-auto h-10 shrink-0 border-amber-500/40" data-testid="diary-pending-sync">
                    <Wand2 size={14} /> Doplnit z docházky
                  </Button>
                )}
              </div>
            );
          })()}
          {readOnly && (
            <div className="flex items-center gap-2 text-sm bg-secondary border border-border text-muted-foreground rounded-[20px] p-3" data-testid="diary-readonly-banner">
              <StickyNote size={16} className="shrink-0" /> Náhled deníku — zápisy může vytvářet a upravovat pouze stavbyvedoucí nebo majitel.
            </div>
          )}
          {!savedAt && !readOnly && (
            <div className="flex items-center gap-2 text-sm bg-brand/10 border border-brand/30 text-foreground rounded-[20px] p-3" data-testid="diary-draft-banner">
              <Sparkles size={16} className="text-brand shrink-0" /> Koncept — automaticky předvyplněno z počasí a docházky. Zkontrolujte a uložte.
            </div>
          )}

          {/* Weather */}
          <div className="bg-card border border-border rounded-[20px] p-4" data-testid="diary-weather">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2 mb-3">
              <h3 className="font-bold flex items-center gap-2 min-w-0">
                <CloudSun size={18} className="text-brand shrink-0" /> Počasí
                {!meta.hasGps && <span className="text-xs font-normal text-muted-foreground">(zakázka nemá GPS — vyplňte ručně)</span>}
              </h3>
              {!readOnly && (
                <Button type="button" variant="outline" size="sm" onClick={addExtra} className="self-start shrink-0" data-testid="diary-weather-add">
                  <Plus size={14} /> Přidat hodinu
                </Button>
              )}
            </div>
            {wxErr && !readOnly && meta.hasGps && (
              <div className="mb-3 flex flex-col sm:flex-row sm:items-center gap-2 rounded-[12px] bg-secondary px-3 py-2.5 text-sm" role="status" data-testid="diary-weather-error">
                <span className="min-w-0 flex-1 text-muted-foreground">Počasí se teď nepodařilo načíst. Zkuste to znovu, nebo ho vyplňte ručně.</span>
                <Button type="button" variant="outline" disabled={wxBusy} onClick={() => autoWeather(date)} className="w-full sm:w-auto h-10 shrink-0" data-testid="diary-weather-retry">
                  {wxBusy ? <Loader2 size={16} className="animate-spin motion-reduce:animate-none" /> : null} Zkusit znovu
                </Button>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              {[["07", "7:00"], ["12", "12:00"], ["17", "17:00"]].map(([k, lbl]) => (
                <div key={k} className="space-y-1">
                  <Label className="text-xs">{lbl}</Label>
                  <div className="flex items-center gap-1">
                    <Input type="number" value={form.weather["t" + k]} onChange={(e) => setW("t" + k, e.target.value)} className="h-10" data-testid={`diary-weather-t${k}`} readOnly={readOnly} />
                    <span className="text-muted-foreground text-sm">°C</span>
                  </div>
                  <Input value={form.weather["d" + k]} onChange={(e) => setW("d" + k, e.target.value)} placeholder="stav" className="h-9 text-xs" data-testid={`diary-weather-d${k}`} readOnly={readOnly} />
                </div>
              ))}
            </div>
            {(form.weather.extra || []).length > 0 && (
              <div className="mt-3 space-y-2" data-testid="diary-weather-extra">
                {form.weather.extra.map((x, i) => (
                  <div key={i} className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:grid-cols-[120px_120px_minmax(0,1fr)_auto] gap-2 items-center" data-testid={`diary-weather-extra-${i}`}>
                    <div className="min-w-0"><Input type="time" step={300} value={x.time} onChange={(e) => setExtra(i, "time", e.target.value)} className="h-10" aria-label="Hodina" readOnly={readOnly} /></div>
                    <div className="flex items-center gap-1 min-w-0">
                      <Input type="number" value={x.t} onChange={(e) => setExtra(i, "t", e.target.value)} className="h-10 min-w-0" aria-label="Teplota °C" readOnly={readOnly} />
                      <span className="text-muted-foreground text-sm">°C</span>
                    </div>
                    <Input value={x.d} onChange={(e) => setExtra(i, "d", e.target.value)} placeholder="stav" className="h-10 col-span-2 sm:col-span-1 row-start-2 sm:row-start-auto" aria-label="Stav" readOnly={readOnly} />
                    {!readOnly && (
                      <button type="button" onClick={() => delExtra(i)} aria-label="Smazat hodinu" className="h-10 w-10 grid place-items-center rounded-full text-muted-foreground hover:text-destructive col-start-3 row-start-1 sm:col-start-auto sm:row-start-auto">
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3 mt-3">
              <div><Label className="text-xs">Min °C</Label><Input type="number" value={form.weather.tmin} onChange={(e) => setW("tmin", e.target.value)} className="h-10 mt-1" data-testid="diary-weather-tmin" readOnly={readOnly} /></div>
              <div><Label className="text-xs">Max °C</Label><Input type="number" value={form.weather.tmax} onChange={(e) => setW("tmax", e.target.value)} className="h-10 mt-1" data-testid="diary-weather-tmax" readOnly={readOnly} /></div>
            </div>
          </div>

          {/* Workers */}
          <div className="bg-card border border-border rounded-[20px] p-4" data-testid="diary-workers">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h3 className="font-bold flex items-center gap-2"><Users size={18} className="text-brand" /> Pracovníci na stavbě</h3>
              {/* 410: jedno rozbalovací tlačítko — Vybrat z týmu (parta → docházka) / Nahrát z docházky */}
              {!readOnly && (
                <div className="flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" data-testid="diary-add-people"><UserPlus size={14} /> Přidat lidi <ChevronDown size={14} className="opacity-60" /></Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="min-w-[240px]">
                    {canCrew && <DropdownMenuItem onClick={() => crewRef.current?.openPick()} data-testid="diary-crew-pick"><UserPlus size={15} className="mr-2 text-brand" /> Vybrat z týmu</DropdownMenuItem>}
                    <DropdownMenuItem onClick={syncFromAttendance} data-testid="diary-sync-attendance"><Wand2 size={15} className="mr-2 text-brand" /> Nahrát z docházky</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button variant="ghost" size="sm" onClick={addManualWorker} data-testid="diary-add-worker" title="Jméno a hodiny jen do deníku — do docházky se nezapíše">
                  <Plus size={14} /> {canCrew ? "Řádek bez docházky" : "Přidat ručně"}</Button>
                </div>
              )}
            </div>
            {canCrew && <div className={crewCount ? "mb-3 pb-3 border-b border-border" : ""}><DiaryCrew ref={crewRef} projectId={projectId} date={date} onRows={onCrewRows} onSaved={onCrewSaved} onCount={setCrewCount} /></div>}
            <div className="space-y-2">
              {form.workers.map((w, i) => (canCrew && !w.manual && crewNames.has(w.name)) ? null : (
                <div key={i} className="flex items-center gap-2" data-testid={`diary-worker-${i}`}>
                  <Input value={w.name} onChange={(e) => setWorker(i, "name", e.target.value)} placeholder="Jméno" className="h-10 flex-1" readOnly={!w.manual || readOnly} />
                  <div className="flex items-center gap-1 w-24">
                    <Input type="number" value={w.hours} onChange={(e) => setWorker(i, "hours", e.target.value)} className="h-10" readOnly={!w.manual || readOnly} />
                    <span className="text-muted-foreground text-xs">h</span>
                  </div>
                  {w.manual && !readOnly ? (
                    <button onClick={() => removeWorker(i)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`diary-remove-worker-${i}`}><Trash2 size={16} /></button>
                  ) : (
                    <span className="text-[10px] uppercase font-bold text-emerald-500 w-10 text-center shrink-0">{w.manual ? "" : "auto"}</span>
                  )}
                </div>
              ))}
              {form.workers.filter((w) => !(canCrew && !w.manual && crewNames.has(w.name))).length === 0 && !(canCrew && crewCount) && <p className="text-sm text-muted-foreground">{readOnly ? "Pro tento den není žádná docházka." : canCrew ? "Pro tento den není žádná docházka. Přidejte lidi z týmu (Přidat lidi), nebo řádek bez docházky." : "Pro tento den není žádná docházka. Přidejte ručně."}</p>}
            </div>
          </div>

          {/* Materiál — čistá spotřeba ze skladové knihy (jeden řádek na položku,
              zadání 143) + volná hlášení; řádky ze skladu nejdou z deníku smazat,
              aby se nerozjel sklad (zadání 142) */}
          <div className="bg-card border border-border rounded-[20px] p-4" data-testid="diary-materials">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <h3 className="font-bold flex items-center gap-2"><Package size={18} className="text-brand" /> Materiál</h3>
              {!matReadOnly && (
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => openMatDialog("stock")} data-testid="diary-mat-issue"><Package size={14} /> Vydat ze skladu</Button>
                  <Button variant="ghost" size="sm" onClick={() => openMatDialog("free")} data-testid="diary-mat-note"><Plus size={14} /> Řádek bez skladu</Button>
                </div>
              )}
            </div>
            {matData.items.length === 0 ? (
              <p className="text-sm text-muted-foreground" data-testid="diary-mat-empty">Pro tento den není zapsaný žádný materiál.</p>
            ) : (
              <div className="space-y-1.5">
                {matData.items.map((r, idx) => {
                  if (r.kind === "material") {
                    return (
                      <div key={r.key} className="flex items-center gap-2 flex-wrap text-sm py-1.5 border-b border-border/60 last:border-0" data-testid={`diary-material-${idx}`}>
                        <span className="font-semibold">{r.name}</span>
                        <span className="tabular-nums text-muted-foreground" data-testid={`diary-material-qty-${idx}`}>
                          {fmtQty(r.qty)} {r.unit}
                          {r.returned > 0 && <span className="ml-1 text-xs text-muted-foreground/70" data-testid={`diary-material-detail-${idx}`}>(vydáno {fmtQty(r.issued)}, vratka {fmtQty(r.returned)})</span>}
                        </span>
                        <span className="text-[11px] px-2 py-0.5 rounded-full font-semibold bg-secondary text-muted-foreground">ze skladu</span>
                        <span className="text-xs text-muted-foreground truncate flex-1 min-w-0">{(r.people || []).join(", ")}</span>
                        <Link to={`/sklad?movement=${r.movement_ids[0]}`} className="text-xs text-brand hover:underline shrink-0" data-testid={`diary-material-open-${idx}`}>Otevřít ve Skladu</Link>
                      </div>
                    );
                  }
                  const b = MAT_BADGE[r.status] || MAT_BADGE.noted;
                  return (
                    <div key={r.id} className="flex items-center gap-2 flex-wrap text-sm py-1.5 border-b border-border/60 last:border-0" data-testid={`diary-mat-${r.id}`}>
                      <span className="font-semibold">{r.name}</span>
                      <span className="tabular-nums text-muted-foreground">{fmtQty(r.qty)} {r.unit}</span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${b.cls}`}>{b.label}</span>
                      <span className="text-xs text-muted-foreground truncate flex-1 min-w-0">{r.user_name}{r.dismiss_note ? ` · ${r.dismiss_note}` : ""}</span>
                      {!matReadOnly && (
                        <span className="flex items-center gap-1 shrink-0">
                          {r.status === "pending" && (
                            <>
                              <button onClick={() => resolveMat(r, "confirm")} title="Odepsat ze skladu" data-testid={`diary-mat-confirm-${r.id}`}
                                className="h-7 px-2 rounded-md bg-success/15 text-success hover:bg-success/25 inline-flex items-center gap-1 text-xs font-semibold"><Check size={13} /> Odepsat</button>
                              <button onClick={() => resolveMat(r, "dismiss")} title="Už odepsáno ručně — bez pohybu skladu" data-testid={`diary-mat-dismiss-${r.id}`}
                                className="h-7 px-2 rounded-md bg-secondary text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-xs font-semibold"><X size={13} /> Už odepsáno</button>
                            </>
                          )}
                          <button onClick={() => setMatDel(r)} title="Smazat záznam" data-testid={`diary-mat-delete-${r.id}`}
                            className="h-7 w-7 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 inline-flex items-center justify-center"><Trash2 size={13} /></button>
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 186: Kdo co dělal — jeden řádek na člověka (hodiny + popisy z docházky / výkazu) */}
          {meta.people.length > 0 && (
            <div className="bg-card border border-border rounded-[20px] p-4" data-testid="diary-people">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <h3 className="font-bold flex items-center gap-2"><Users size={18} className="text-brand" /> Kdo co dělal</h3>
                {!readOnly && meta.people.some((p) => p.text) && (
                  <button type="button" data-testid="diary-people-insert"
                    onClick={() => setForm((f) => ({ ...f, work_performed: meta.people.filter((p) => p.text).map((p) => `${p.name}: ${p.text}`).join("\n") }))}
                    className="text-[13px] font-bold text-brand hover:underline">Vložit do Provedených prací</button>
                )}
              </div>
              <div className="divide-y divide-border">
                {meta.people.map((p, k) => (
                  <button key={k} type="button" onClick={() => setOpenPerson(openPerson === k ? null : k)} data-testid={`diary-person-${k}`}
                    className="w-full text-left flex items-baseline gap-3 py-2 min-w-0">
                    <span className="font-semibold text-sm whitespace-nowrap shrink-0">{p.name}</span>
                    <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap shrink-0">{String(p.hours).replace(".", ",")} h</span>
                    <span className={`text-sm text-muted-foreground min-w-0 flex-1 ${openPerson === k ? "whitespace-normal break-words" : "truncate"}`}>
                      {p.text || <i>bez popisu</i>}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Work performed */}
          <div className="bg-card border border-border rounded-[20px] p-4" data-testid="diary-work-card">
            <h3 className="font-bold flex items-center gap-2 mb-2"><Hammer size={18} className="text-brand" /> Provedené práce</h3>
            {meta.suggestion && (
              <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1"><Sparkles size={12} className="text-brand" /> Návrh z reportů docházky předvyplněn — upravte dle potřeby.</p>
            )}
            <Textarea value={form.work_performed} onChange={(e) => setForm({ ...form, work_performed: e.target.value })} rows={4} data-testid="diary-work-performed" placeholder="Co se dnes na stavbě dělalo…" readOnly={readOnly} />
          </div>

          {/* Notes */}
          <div className="bg-card border border-border rounded-[20px] p-4">
            <h3 className="font-bold flex items-center gap-2 mb-2"><StickyNote size={18} className="text-brand" /> Poznámky</h3>
            <p className="text-xs text-muted-foreground mb-2">Materiál, překážky/prostoje, BOZP, vícepráce — vše dohromady.</p>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={3} data-testid="diary-notes" readOnly={readOnly} />
          </div>

          {/* Fotky dne — jedna banka fotek (diary_photos) sdílená s Galerií (zadání 141).
              186: dvě sekce — Čeká na schválení (investor nevidí) a V deníku (vidí, jde do tisku);
              Nepoužité („Nechci") sbalené, zůstávají v Galerii. */}
          {(() => {
            const pending = attPhotos.filter((p) => p.status !== "approved" && p.status !== "rejected");
            const approved = attPhotos.filter((p) => p.status === "approved");
            const rejected = attPhotos.filter((p) => p.status === "rejected");
            const grid = (list, testid) => (
              <div data-testid={testid}><GalleryGrid photos={list} canEye={!readOnly} mode={photoSel ? "select" : null} selected={selPhotos}
                onToggleSelect={toggleSelPhoto} onOpen={(p) => setViewer({ urls: list.map((x) => x.url), start: list.indexOf(p) })}
                onEye={(p) => approvePhotos([p.id], p.status !== "approved")}
                gridClass="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3" /></div>
            );
            return (
              <div className="bg-card border border-border rounded-[20px] p-4 space-y-4" data-testid="diary-photos">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <h3 className="font-bold flex items-center gap-2"><Camera size={18} className="text-brand" /> Fotky dne</h3>
                  {!readOnly && (
                    <div className="flex gap-2">
                      {attPhotos.length > 0 && (
                        <Button variant={photoSel ? "default" : "outline"} size="sm" onClick={() => { setPhotoSel((x) => !x); setSelPhotos(new Set()); }} data-testid="diary-photos-select">
                          {photoSel ? "Hotovo" : "Vybrat"}
                        </Button>
                      )}
                      {/* Jedno tlačítko: iOS nativní nabídka, Android Vyfotit / Galerie / Soubor, PC výběr souboru. */}
                      {photoPick.inputs}
                      <Button variant="outline" size="sm" onClick={photoPick.open} disabled={uploading} className="flex-1 md:flex-none" data-testid="diary-add-photo">
                        <Camera size={14} /> {uploading ? "Nahrávám…" : "Nahrát fotky"}
                      </Button>
                    </div>
                  )}
                </div>
                {photoSel && selPhotos.size > 0 && (
                  <div className="flex items-center gap-2 flex-wrap rounded-[14px] bg-brand/10 border border-brand/30 px-3 py-2" data-testid="diary-photos-bulk">
                    <span className="font-bold text-sm">{plur(selPhotos.size, "vybraná fotka", "vybrané fotky", "vybraných fotek")}</span>
                    <Button size="sm" className="rounded-full bg-success hover:bg-success/90 text-white" onClick={() => bulkApprove(true)} data-testid="diary-bulk-approve">
                      <Check size={14} /> Do deníku
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => bulkApprove(false)} data-testid="diary-bulk-hide">
                      <EyeOff size={14} /> Z deníku pryč
                    </Button>
                    {user?.role === "OWNER" && (
                      <Button size="sm" variant="outline" className="rounded-full border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={deletePhotos} data-testid="diary-bulk-delete">
                        <Trash2 size={14} /> Smazat ({selPhotos.size})
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="rounded-full ml-auto" onClick={() => { setSelPhotos(new Set()); setPhotoSel(false); }} data-testid="diary-bulk-clear">
                      <X size={14} /> Zrušit výběr
                    </Button>
                  </div>
                )}
                {attPhotos.length === 0 && <p className="text-sm text-muted-foreground" data-testid="diary-photos-empty">Zatím žádné fotky.</p>}
                {drafts.length > 0 && (
                  <button type="button" onClick={openDefectTask} data-testid="diary-defects-chip"
                    className="w-full md:w-auto h-11 px-4 rounded-[14px] border-[1.5px] border-dashed border-brand text-brand bg-brand/10 font-bold text-sm">
                    Závady · {drafts.length} → vytvořit úkol
                  </button>
                )}

                {pending.length > 0 && (
                  <section className="space-y-2.5" data-testid="diary-photos-pending">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="w-2.5 h-2.5 rounded-full bg-amber-500 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-bold">Čeká na schválení · {pending.length}</div>
                        <div className="text-[12.5px] text-muted-foreground">Investor je nevidí a nejdou do tisku.</div>
                      </div>
                      {!readOnly && (
                        <Button onClick={() => setViewer({ urls: pending.map((x) => x.url), start: 0, review: true })} data-testid="diary-review-start"
                          className="w-full md:w-auto h-11 rounded-full font-bold">Projít a schválit · {pending.length}</Button>
                      )}
                    </div>
                    {grid(pending, "diary-grid-pending")}
                  </section>
                )}

                {approved.length > 0 && (
                  <section className="space-y-2.5" data-testid="diary-photos-approved">
                    <div className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-full bg-success shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="font-bold">V deníku · {approved.length}</div>
                        <div className="text-[12.5px] text-muted-foreground">Vidí investor · jdou do tisku deníku.</div>
                      </div>
                    </div>
                    {grid(approved, "diary-grid-approved")}
                  </section>
                )}

                {rejected.length > 0 && (
                  <section className="space-y-2.5" data-testid="diary-photos-rejected">
                    <button type="button" onClick={() => setShowRejected((x) => !x)} data-testid="diary-rejected-toggle"
                      className="w-full flex items-center gap-2 h-11 px-3 rounded-[14px] border border-border text-sm text-muted-foreground font-semibold hover:text-foreground">
                      <span className="flex-1 text-left">Nepoužité · {rejected.length} <span className="font-normal">(zůstávají v Galerii)</span></span>
                      <ChevronDown size={16} className={`transition-transform motion-reduce:transition-none ${showRejected ? "rotate-180" : ""}`} />
                    </button>
                    {showRejected && <div className="opacity-70">{grid(rejected, "diary-grid-rejected")}</div>}
                  </section>
                )}
              </div>
            );
          })()}

          <div className="sticky bottom-[calc(var(--bottom-nav-h,4.5rem)+env(safe-area-inset-bottom))] md:bottom-0 bg-background/80 backdrop-blur py-2 flex flex-col items-center gap-2">
            <span className="text-center text-xs text-muted-foreground">{savedAt ? `Zapsal ${author} · ${savedAt.slice(0, 16).replace("T", " ")}` : "Neuloženo"}</span>
            {!readOnly && <Button onClick={() => save()} disabled={saving} data-testid="diary-save" className="w-full md:w-auto md:px-10 h-12 font-bold"><Save size={16} /> {saving ? "Ukládám…" : "Uložit zápis"}</Button>}
          </div>
        </>
      )}

      {/* Dialog: smazání hlášení materiálu (u odepsaného = storno s vratkou) */}
      <Dialog open={!!matDel} onOpenChange={(o) => !o && setMatDel(null)}>
        <DialogContent data-testid="diary-mat-delete-dialog">
          <DialogHeader><DialogTitle>Smazat záznam materiálu?</DialogTitle></DialogHeader>
          {matDel && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {matDel.status === "confirmed"
                  ? <>Záznam „{matDel.name} — {matDel.qty} {matDel.unit}" už je odepsaný ze skladu. Smazáním se provede <b className="text-foreground">storno: množství se vrátí zpět na sklad</b> a náklad zakázky se poníží (vratka).</>
                  : <>Záznam „{matDel.name} — {matDel.qty} {matDel.unit}" se smaže z deníku. Sklad se nezmění.</>}
              </p>
              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                <Button variant="outline" onClick={() => setMatDel(null)} className="h-11">Zrušit</Button>
                <Button variant="destructive" onClick={deleteMat} className="h-11 font-bold" data-testid="diary-mat-delete-confirm">
                  {matDel.status === "confirmed" ? "Smazat a vrátit na sklad" : "Smazat"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog: přidání materiálu (ze skladu / volný řádek) */}
      <Dialog open={!!matDialog} onOpenChange={(o) => !o && setMatDialog(null)}>
        <DialogContent data-testid="diary-mat-dialog">
          <DialogHeader><DialogTitle>{matDialog?.mode === "stock" ? "Vydat materiál ze skladu" : "Materiál mimo sklad"}</DialogTitle></DialogHeader>
          {matDialog && (
            <div className="space-y-3">
              {matDialog.mode === "stock" ? (
                <div>
                  <Label>Položka skladu zakázky</Label>
                  <Select value={matDialog.stock_item_id} onValueChange={(v) => setMatDialog({ ...matDialog, stock_item_id: v })}>
                    <SelectTrigger className="h-11 mt-1" data-testid="diary-mat-item"><SelectValue placeholder="— vyberte položku —" /></SelectTrigger>
                    <SelectContent>
                      {matStock.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} (skladem {s.qty} {s.unit})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {matStock.length === 0 && <p className="text-xs text-muted-foreground mt-1">Sklad zakázky je prázdný — materiál na něj převede stavbyvedoucí z centrálního skladu.</p>}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
                  <div><Label>Název materiálu</Label><Input value={matDialog.name} onChange={(e) => setMatDialog({ ...matDialog, name: e.target.value })} className="h-11 mt-1" placeholder="např. beton C25/30 z domíchávače" data-testid="diary-mat-name" /></div>
                  <div><Label>Jednotka</Label><Input value={matDialog.unit} onChange={(e) => setMatDialog({ ...matDialog, unit: e.target.value })} className="h-11 mt-1 w-full sm:w-24" placeholder="ks" /></div>
                </div>
              )}
              <div><Label>Množství</Label><Input type="number" min={0} value={matDialog.qty} onChange={(e) => setMatDialog({ ...matDialog, qty: e.target.value })} className="h-11 mt-1 w-full min-w-0" data-testid="diary-mat-qty" /></div>
              {matDialog.mode === "stock" && (
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input type="checkbox" checked={matDialog.writeoff} onChange={(e) => setMatDialog({ ...matDialog, writeoff: e.target.checked })} data-testid="diary-mat-writeoff" />
                  Rovnou odepsat ze skladu (výdej na zakázku)
                </label>
              )}
              <Button onClick={() => guardMat(saveMatDialog)} disabled={matBusy} className="w-full h-11 font-bold" data-testid="diary-mat-save">
                {matDialog.mode === "stock" && matDialog.writeoff ? "Vydat a odepsat" : "Zapsat do deníku"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={rangeOpen} onOpenChange={setRangeOpen}>
        <DialogContent  data-testid="diary-range-dialog">
          <DialogHeader><DialogTitle>Tisk deníku za období</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Od</Label><Input type="date" value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} className="h-11 mt-1" data-testid="diary-range-from" /></div>
            <div><Label>Do</Label><Input type="date" value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} className="h-11 mt-1" data-testid="diary-range-to" /></div>
          </div>
          <p className="text-xs text-muted-foreground" data-testid="diary-range-photos-hint">{printPhotos ? "Tiskne se s fotkami." : "Tiskne se bez fotek."} Změníte v nabídce Tisk.</p>
          <Button onClick={printRange} disabled={!!printing} className="w-full h-11 font-bold" data-testid="diary-range-export"><FileDown size={16} /> Stáhnout PDF</Button>
        </DialogContent>
      </Dialog>

      {uploadProg && (
        <div className="fixed inset-0 z-[95] bg-black/45 flex items-center justify-center px-4 pt-[max(1.5rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]" data-testid="diary-upload-progress">
          <div className="bg-card border border-border rounded-[18px] p-5 w-full max-w-[340px] text-center shadow-2xl">
            <div className="font-bold mb-1">Nahrávám fotky…</div>
            <div className="text-sm text-muted-foreground mb-3 tabular-nums">
              {Math.min(uploadProg.done + 1, uploadProg.total)} z {uploadProg.total}
            </div>
            <div className="h-2 rounded-full bg-secondary overflow-hidden">
              <div className="h-full bg-brand transition-all duration-300"
                style={{ width: `${Math.round((uploadProg.done / uploadProg.total) * 100)}%` }} />
            </div>
            <p className="text-xs text-muted-foreground mt-3">Nezavírejte stránku, dokud se nahrávání nedokončí.</p>
          </div>
        </div>
      )}

      <PhotoPinViewer open={!!viewer} onClose={() => setViewer(null)} photos={viewer?.urls || []} start={viewer?.start || 0}
        title={formatDate(date)} focusDraft={viewer?.focusDraft ?? null}
        editable={!readOnly && canLeadTasks(user)} drafts={drafts} setDrafts={setDrafts} onDone={openDefectTask}
        review={viewer?.review && !readOnly ? {
          onDecide: (url, ok) => { const p = attPhotos.find((x) => x.url === url); if (p) approvePhotos([p.id], ok, { quiet: true, rejected: !ok }); },
          onFinish: () => { setViewer(null); toast.success("Hotovo — všechny fotky dne jsou prošlé."); },
        } : undefined}
        info={(url) => photoInfo(attPhotos.find((x) => x.url === url))}
        diary={readOnly ? null : { isIn: (url) => attPhotos.find((x) => x.url === url)?.status === "approved",
          toggle: (url) => { const p = attPhotos.find((x) => x.url === url); if (p) approvePhotos([p.id], p.status !== "approved"); } }}
        onShare={readOnly ? undefined : (url) => { const p = attPhotos.find((x) => x.url === url); if (p) shareToWhatsApp([p.id]); }}
        onDelete={user?.role === "OWNER" ? (url) => {
          const p = attPhotos.find((x) => x.url === url); if (!p) return;
          setAttPhotos((arr) => arr.filter((x) => x.id !== p.id));
          setViewer((v) => (v ? { ...v, urls: v.urls.filter((u) => u !== url) } : v));
          api.post("/photos/delete", { photo_ids: [p.id] }).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); load(); });
        } : undefined} />

      {/* 187: úkol ze závad — stejná pole jako Úkoly › Nový úkol */}
      <Dialog open={taskOpen} onOpenChange={setTaskOpen}>
        <DialogContent sheet className="max-w-lg max-md:pt-8 overflow-y-auto md:max-h-[92vh]" onOpenAutoFocus={(e) => e.preventDefault()} data-testid="diary-defect-task-dialog">
          <DialogHeader className="sr-only"><DialogTitle>Nový úkol ze závad</DialogTitle></DialogHeader>
          <DefectTaskForm drafts={drafts} setDrafts={setDrafts} projectId={projectId} projectName={projectName}
            assignee={draftAuthor} sourceKind="diary" sourceId={date} returnTask={returnTask}
            onEditDraft={(k) => { const urls = attPhotos.map((p) => p.url); setTaskOpen(false); setViewer({ urls, start: Math.max(0, urls.indexOf(drafts[k]?.photo_url)), focusDraft: k }); }}
            onCreated={() => setTaskOpen(false)} bare />
        </DialogContent>
      </Dialog>
    </div>
  );
}
